import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Divider,
  Box,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Link,
  useMediaQuery,
  Skeleton
} from '@mui/material'
import { useDrop } from 'react-dnd'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArticleIcon from '@mui/icons-material/Article'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import BusinessIcon from '@mui/icons-material/Business'
import ShareIcon from '@mui/icons-material/Share'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import SyncIcon from '@mui/icons-material/Sync'
import CachedIcon from '@mui/icons-material/Cached'
import ErrorIcon from '@mui/icons-material/Error'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import LinkIcon from '@mui/icons-material/Link'
import SecurityIcon from '@mui/icons-material/Security'
import CloseIcon from '@mui/icons-material/Close'
import CookieService from 'services/CookieService'
import { useSelector } from 'react-redux'
import ReactDOM from 'react-dom'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import APIService from 'services/APIService'
import FolderPermissionsIndicator from './files/FolderPermissionsIndicator'
import SharePointReauthModal from './SharePoint/SharePointReauthModal'
import SharePointViewInfo from './SharePoint/SharePointViewInfo'
import SharePointChangeFolderModal from './SharePoint/SharePointChangeFolderModal'
import SharePointIcon from './SharePoint/SharePointIcon'
import { isSharePointTokenExpired } from 'utils/errors'

const CustomViewFolders = ({
  treeData,
  selectedNode,
  onNodeSelect,
  onFolderCreate,
  onFolderEdit,
  onFolderDelete,
  canEdit,
  loading,
  initialLoading = false,
  customViews,
  selectedView,
  onViewSelect,
  onViewCreate,
  onViewEdit,
  onViewDelete,
  onSharePointIntegrate,
  onSharePointAccessEdit,
  onFileDrop,
  expandedFolders,
  setExpandedFolders,
  isReadOnly = false,
  files = [],
  onRefresh,
  onCustomViewRefresh,
  filesLoading = false,
  filesLoaded = false,
  loadedCustomFolders = new Set(),
  enrichedFolders = new Set(),
  loadingCustomFolders = new Set(),
  searchResults = null,
  isSearching = false,
  bp: bpProp = null
}) => {
  const selectedBp = useSelector(getSelectedBp)
  const bp = bpProp ?? selectedBp
  const currentUser = useSelector(getCurrentUser)
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null)

  // Build a Set of search result file IDs for fast matching count lookups
  const searchResultIds = React.useMemo(
    () => isSearching && searchResults ? new Set(searchResults.map(f => f.id)) : null,
    [isSearching, searchResults]
  )

  // Recursively count files in a folder (and its descendants) that match search results
  // Returns -1 only if fileIds property is completely missing (old cached tree without the field)
  const getMatchingCountForFolder = useCallback((folder) => {
    if (!searchResultIds) return 0
    let count = 0
    let hasFileIdsField = false
    const walk = (f) => {
      if (!f) return
      // Check if the fileIds field exists at all (even empty array means data is available)
      if (f.fileIds !== undefined) hasFileIdsField = true
      const ids = f.fileIds || (f.files || []).map(file => typeof file === 'object' ? file.id : file)
      ids.forEach(id => {
        if (id && searchResultIds.has(id)) count += 1
      })
      ;(f.children || []).forEach(walk)
    }
    walk(folder)
    return hasFileIdsField ? count : -1
  }, [searchResultIds])

  // Check if current user is owner of the building passport
  const isOwner =
    bp?.owners?.some(owner => owner.id === currentUser?.id) ||
    bp?.owner?.id === currentUser?.id
  const [contextMenuItem, setContextMenuItem] = useState(null)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [viewSheetOpen, setViewSheetOpen] = useState(false)
  const [changeFolderOpen, setChangeFolderOpen] = useState(false)

  // Folder user counts state
  const [folderUserCounts, setFolderUserCounts] = useState({})

  // SharePoint sync state
  const [syncStatus, setSyncStatus] = useState(
    selectedView?.syncStatus || selectedView?.sync_status || {}
  )
  const [lastSync, setLastSync] = useState(
    selectedView?.lastSyncAt || selectedView?.last_sync_at
  )
  const [pollingSyncStatus, setPollingSyncStatus] = useState(false)
  const pollingIntervalRef = useRef(null)
  const [reauthModalOpen, setReauthModalOpen] = useState(false)
  const [resyncConfirmOpen, setResyncConfirmOpen] = useState(false)

  // Determine if this is a SharePoint view
  const isSharePointView = selectedView?.integrationType === 'sharepoint'

  // Auto-show reauth modal when token_expired status is detected
  useEffect(() => {
    if (
      isSharePointView &&
      (syncStatus?.status === 'token_expired' || syncStatus?.requiresReauth)
    ) {
      setReauthModalOpen(true)
    }
  }, [syncStatus, isSharePointView])

  // Count actual files in tree data recursively
  const countFilesInTree = folders => {
    let count = 0
    if (!folders) return count

    folders.forEach(folder => {
      // Count files in this folder
      if (folder.files && Array.isArray(folder.files)) {
        count += folder.files.length
      }
      // Recursively count files in children
      if (folder.children && Array.isArray(folder.children)) {
        count += countFilesInTree(folder.children)
      }
    })

    return count
  }

  // Calculate actual visible file count
  const actualFileCount = treeData
    ? countFilesInTree(treeData.folders || [])
    : 0

  // Use parent's expanded state if provided, otherwise use local state
  const [localExpandedFolders, setLocalExpandedFolders] = useState(new Set())
  const currentExpandedFolders = expandedFolders || localExpandedFolders
  const currentSetExpandedFolders =
    setExpandedFolders || setLocalExpandedFolders
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null)
  const [unsupportedFilesModalOpen, setUnsupportedFilesModalOpen] =
    useState(false)
  const [failedFilesExpanded, setFailedFilesExpanded] = useState(false)

  const openViewSelector = (e) => {
    if (isMobile) {
      setViewSheetOpen(true)
      return
    }
    handleViewMenuOpen(e)
  }

  const closeViewSelector = () => {
    setViewMenuAnchor(null)
    setViewSheetOpen(false)
  }

  // Note: Expanded folders restoration is handled by the parent CustomViews component
  useEffect(() => {
    console.log(
      'CustomViewFolders received expandedFolders from parent:',
      expandedFolders ? [...expandedFolders] : 'none'
    )
    console.log('Current expanded folders state:', [...currentExpandedFolders])
  }, [expandedFolders, currentExpandedFolders])

  // SharePoint sync effects
  useEffect(() => {
    console.log('selectedView changed:', {
      syncStatus: selectedView?.syncStatus,
      sync_status: selectedView?.sync_status,
      lastSyncAt: selectedView?.lastSyncAt,
      last_sync_at: selectedView?.last_sync_at
    })

    const initialSyncStatus =
      selectedView?.syncStatus || selectedView?.sync_status || {}
    setSyncStatus(initialSyncStatus)
    setLastSync(selectedView?.lastSyncAt || selectedView?.last_sync_at)

    // Check if we should start polling immediately based on initial sync status
    if (
      isSharePointView &&
      (initialSyncStatus.status === 'pending' ||
        initialSyncStatus.status === 'running')
    ) {
      console.log(
        'Initial view has pending/running status, enabling polling immediately'
      )
      setPollingSyncStatus(true)
    } else {
      // Reset polling state when view changes (only if not pending/running)
      setPollingSyncStatus(false)
    }

    // For SharePoint views, immediately fetch fresh sync status
    if (isSharePointView && bp?.id && selectedView?.id) {
      const fetchSyncStatus = async () => {
        try {
          const api = APIService()
          const response = await api.get(
            `/building-passport/${bp.id}/custom-views/${selectedView.id}/sharepoint/status`
          )
          const statusData = response.data?.data
          if (statusData?.sync_status) {
            console.log('Initial sync status fetch:', statusData.sync_status)
            setSyncStatus(statusData.sync_status)
            if (statusData.last_sync_at) {
              setLastSync(statusData.last_sync_at)
            }

            // Only start polling if the status is actually pending or running
            if (
              statusData.sync_status.status === 'pending' ||
              statusData.sync_status.status === 'running'
            ) {
              console.log('Initial status is pending/running, enabling polling')
              setPollingSyncStatus(true)
            }
          }
        } catch (error) {
          console.error('Failed to fetch initial sync status:', error)
          setSyncStatus({
            status: 'error',
            message: 'Failed to fetch sync status'
          })
        }
      }
      fetchSyncStatus()
    }
  }, [selectedView, isSharePointView, bp?.id])

  // Fetch folder user counts
  const fetchFolderUserCounts = useCallback(() => {
    if (bp?.id && selectedView?.id && isOwner) {
      const api = APIService()
      api
        .get(`/building-passport/${bp.id}/custom-views/${selectedView.id}/folders/user-counts`)
        .then(resp => {
          const data = resp?.data?.data || {}

          // Convert array to object for easy lookup
          const folderCounts = (data.folders || []).reduce((acc, item) => {
            acc[item.id] = item.count
            return acc
          }, {})

          setFolderUserCounts({ folders: folderCounts })
        })
        .catch(err => {
          console.error('Failed to fetch folder user counts:', err)
        })
    }
  }, [bp?.id, selectedView?.id, isOwner])

  useEffect(() => {
    fetchFolderUserCounts()
  }, [fetchFolderUserCounts])

  // Poll for sync status updates when sync is running
  useEffect(() => {
    console.log(
      'Polling useEffect - Status:',
      syncStatus.status,
      'Polling:',
      pollingSyncStatus
    )

    // Clean up any existing interval first
    if (pollingIntervalRef.current) {
      console.log('Clearing existing polling interval')
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    // Only start polling if we should be polling AND status is pending/running
    if (
      pollingSyncStatus &&
      (syncStatus.status === 'pending' || syncStatus.status === 'running')
    ) {
      console.log('Starting polling interval...')
      pollingIntervalRef.current = setInterval(async () => {
        console.log('Polling - fetching SharePoint sync status')

        // Fetch the actual sync status from the API
        if (bp?.id && selectedView?.id) {
          try {
            const api = APIService()
            const response = await api.get(
              `/building-passport/${bp.id}/custom-views/${selectedView.id}/sharepoint/status`
            )

            const statusData = response.data?.data
            console.log('Full status response:', statusData)

            // Handle both camelCase and snake_case from API
            const syncStatusData =
              statusData?.syncStatus || statusData?.sync_status

            if (syncStatusData) {
              console.log('Received sync status:', syncStatusData)
              console.log('Status value:', syncStatusData.status)

              // Update the sync status state
              setSyncStatus(syncStatusData)

              // Also update last sync time if available - check both formats
              const lastSyncTime =
                statusData?.lastSyncAt || statusData?.last_sync_at
              if (lastSyncTime) {
                setLastSync(lastSyncTime)
              }

              // Check if we should stop polling based on the new status
              const shouldStopPolling = [
                'success',
                'error',
                'completed',
                'partial_success',
                'idle'
              ].includes(syncStatusData.status)

              if (shouldStopPolling) {
                console.log(
                  'STOPPING POLLING - Received terminal status:',
                  syncStatusData.status
                )
                setPollingSyncStatus(false)

                // Clear the interval immediately using ref
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current)
                  pollingIntervalRef.current = null
                }

                // Trigger a comprehensive refresh when sync completes successfully
                if (
                  syncStatusData.status === 'success' ||
                  syncStatusData.status === 'completed' ||
                  syncStatusData.status === 'partial_success'
                ) {
                  console.log(
                    'SharePoint sync completed successfully, refreshing all data'
                  )

                  // Refresh the custom view object from the database to get updated sync_status
                  if (onCustomViewRefresh) {
                    setTimeout(() => {
                      console.log('Refreshing custom view object from database')
                      onCustomViewRefresh()
                    }, 1000) // Refresh view first to get updated sync_status
                  }

                  // Trigger multiple refreshes and notify parent component to reload tree structure
                  if (onRefresh) {
                    setTimeout(() => {
                      console.log('Triggering first refresh')
                      onRefresh()
                      // Also trigger a custom event to reload tree structure
                      window.dispatchEvent(
                        new CustomEvent('sharepoint-sync-complete', {
                          detail: {
                            customViewId: selectedView?.id,
                            buildingPassportId: bp?.id
                          }
                        })
                      )
                    }, 2000) // Increased from 500ms to 2000ms to allow folder assignments to propagate
                    setTimeout(() => {
                      console.log('Triggering second refresh')
                      onRefresh()
                    }, 4000) // Increased from 1500ms to 4000ms
                    setTimeout(() => {
                      console.log('Triggering third refresh')
                      onRefresh()
                    }, 6000) // Increased from 3000ms to 6000ms
                  }
                } else if (onRefresh) {
                  // For other terminal statuses, just refresh once
                  setTimeout(() => onRefresh(), 500)
                }

                return // Exit early to prevent further polling
              } else {
                console.log(
                  'Continuing to poll, status is:',
                  syncStatusData.status
                )
              }
            }
          } catch (error) {
            console.error('Failed to fetch sync status:', error)
            // Stop polling on error
            setPollingSyncStatus(false)
          }
        }
      }, 3000) // Poll every 3 seconds
    } else if (
      // Stop polling if status is explicitly not pending/running (but not undefined/null)
      syncStatus.status &&
      syncStatus.status !== 'pending' &&
      syncStatus.status !== 'running'
    ) {
      console.log(
        'Status is not pending/running, ensuring polling is stopped. Status:',
        syncStatus.status
      )
      setPollingSyncStatus(false)
    }

    return () => {
      if (pollingIntervalRef.current) {
        console.log('Cleaning up polling interval')
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [syncStatus.status, pollingSyncStatus, bp?.id, selectedView?.id])

  // SharePoint sync functions
  const handleManualSync = async () => {
    const api = APIService()

    if (!bp?.id) {
      setSyncStatus({
        status: 'error',
        message: 'Building Passport ID not available'
      })
      return
    }

    if (!selectedView?.id) {
      setSyncStatus({
        status: 'error',
        message: 'Custom view ID not available'
      })
      return
    }

    console.log('SharePoint sync debug:', {
      buildingPassportId: bp.id,
      customViewId: selectedView.id,
      selectedView: selectedView
    })

    try {
      setSyncStatus({ status: 'pending', message: 'Manual sync initiated' })

      const response = await api.post(
        `/building-passport/${bp.id}/custom-views/${selectedView.id}/sharepoint/sync`
      )

      const data = response.data?.data || response.data
      if (data) {
        // Update with the actual sync status from the API response
        const apiSyncStatus = data.custom_view?.sync_status
        if (apiSyncStatus) {
          setSyncStatus(apiSyncStatus)
        } else {
          setSyncStatus({ status: 'running', message: 'Sync in progress...' })
        }

        setPollingSyncStatus(true) // Start polling for status updates

        // Refresh the view after a brief delay to show the updated sync status
        setTimeout(() => {
          if (onRefresh) {
            onRefresh()
          }
        }, 1000)
      }
    } catch (error) {
      console.error('Manual sync failed:', error)

      // Handle token expiration specifically
      if (isSharePointTokenExpired(error)) {
        setReauthModalOpen(true)
        setSyncStatus({
          status: 'token_expired',
          message:
            'SharePoint credentials have expired. Re-authentication required.'
        })
      } else if (error.response?.status === 429) {
        // Handle rate limiting specifically
        const errorData = error.response?.data
        setSyncStatus({
          status: 'rate_limited',
          message: errorData?.message || 'Daily sync limit reached',
          remaining_syncs: errorData?.remaining_syncs_today || 0,
          next_reset: errorData?.next_reset || 'Tomorrow'
        })
      } else {
        setSyncStatus({
          status: 'error',
          message:
            error.response?.data?.message ||
            error.message ||
            'Failed to initiate sync'
        })
      }
    }
  }

  const handleClearAndResync = () => {
    if (!bp?.id || !selectedView?.id) {
      setSyncStatus({
        status: 'error',
        message: 'Building Passport or custom view ID not available'
      })
      return
    }
    setResyncConfirmOpen(true)
  }

  const confirmClearAndResync = async () => {
    setResyncConfirmOpen(false)
    const api = APIService()

    if (!bp?.id || !selectedView?.id) {
      setSyncStatus({
        status: 'error',
        message: 'Building Passport or custom view ID not available'
      })
      return
    }

    try {
      setSyncStatus({
        status: 'pending',
        message: 'Clearing and re-syncing...'
      })

      // Call the clear and resync endpoint (or full sync with clear flag)
      const response = await api.post(
        `/building-passport/${bp.id}/custom-views/${selectedView.id}/sharepoint/sync`,
        {
          clear_existing: true,
          full_sync: true
        }
      )

      const data = response.data?.data || response.data
      if (data) {
        // Update with the actual sync status from the API response
        const apiSyncStatus = data.custom_view?.sync_status
        if (apiSyncStatus) {
          setSyncStatus(apiSyncStatus)
        } else {
          setSyncStatus({
            status: 'running',
            message: 'Full re-sync in progress...'
          })
        }

        setPollingSyncStatus(true) // Start polling for status updates

        // Refresh the view after a brief delay
        setTimeout(() => {
          if (onRefresh) {
            onRefresh()
          }
        }, 1000)
      }
    } catch (error) {
      console.error('Clear and resync failed:', error)

      // Handle token expiration specifically
      if (isSharePointTokenExpired(error)) {
        setReauthModalOpen(true)
        setSyncStatus({
          status: 'token_expired',
          message:
            'SharePoint credentials have expired. Re-authentication required.'
        })
      } else if (error.response?.status === 429) {
        // Handle rate limiting specifically
        const errorData = error.response?.data
        setSyncStatus({
          status: 'rate_limited',
          message: errorData?.message || 'Daily sync limit reached',
          remaining_syncs: errorData?.remaining_syncs_today || 0,
          next_reset: errorData?.next_reset || 'Tomorrow'
        })
      } else {
        setSyncStatus({
          status: 'error',
          message:
            error.response?.data?.message ||
            error.message ||
            'Failed to clear and resync'
        })
      }
    }
  }

  const getSyncStatusColor = () => {
    switch (syncStatus.status) {
      case 'success':
      case 'completed':
        return 'success'
      case 'partial_success':
        return 'warning'
      case 'error':
        return 'error'
      case 'token_expired':
        return 'error'
      case 'rate_limited':
        return 'warning'
      case 'pending':
      case 'running':
        return 'info'
      default:
        return 'default'
    }
  }

  const getSyncStatusIcon = () => {
    switch (syncStatus.status) {
      case 'success':
      case 'completed':
        return <CheckCircleIcon fontSize='small' />
      case 'partial_success':
        return <ErrorIcon fontSize='small' />
      case 'error':
        return <ErrorIcon fontSize='small' />
      case 'token_expired':
        return <ErrorIcon fontSize='small' />
      case 'rate_limited':
        return <ErrorIcon fontSize='small' />
      case 'pending':
      case 'running':
        return <CloudSyncIcon fontSize='small' />
      default:
        return null
    }
  }

  const formatLastSync = () => {
    if (!lastSync) return 'Never'

    const date = new Date(lastSync)

    // Format: DD/MM/YYYY HH:MM
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }

    return date.toLocaleString('en-GB', options)
  }

  const colorPalette = [
    '#0c132b',
    '#bbeaf0',
    '#36becd',
    '#ffc111',
    '#cd2000',
    '#ffaf7f'
  ]

  const handleContextMenu = (event, item) => {
    event.preventDefault()
    event.stopPropagation()
    // Use mouse position instead of element position for better placement
    setContextMenuAnchor({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6
    })
    setContextMenuItem(item)
  }

  const handleViewMenuOpen = e => {
    setViewMenuAnchor(e.currentTarget)
  }

  const toggleFolderExpanded = folderId => {
    const newExpanded = new Set(currentExpandedFolders)
    console.log('Toggling folder:', folderId, 'current expanded:', [
      ...currentExpandedFolders
    ])
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }

    console.log('New expanded folders:', [...newExpanded])

    // Always use parent's state if available, otherwise fallback to local
    if (setExpandedFolders) {
      setExpandedFolders(newExpanded)
    } else {
      setLocalExpandedFolders(newExpanded)
    }

    // Save expanded folders to cookies for custom view
    if (bp?.id && selectedView?.id) {
      const cookieKey = `customExpandedFolders_${bp.id}_${selectedView.id}`
      const expandedArray = [...newExpanded]
      console.log('Saving to cookies:', cookieKey, expandedArray)
      CookieService.set(cookieKey, expandedArray) // CookieService handles JSON.stringify automatically
    }
  }

  // Droppable Folder Component
  const DroppableFolder = ({ folder, children }) => {
    const [{ isOver, canDrop, isDragging }, drop] = useDrop({
      accept: 'file',
      drop: item => {
        if (onFileDrop) {
          // Handle both single file (legacy) and multi-file drops
          if (item.files && item.files.length > 0) {
            // Multi-file drop - use the first file to trigger the drop
            // The handleFileDrop function will detect and move all selected files
            onFileDrop(item.files[0], folder.id)
          } else {
            // Single file drop (legacy)
            onFileDrop(item.file, folder.id)
          }
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        isDragging: monitor.getItem() !== null
      })
    })

    return (
      <div ref={drop} style={{ position: 'relative' }}>
        {React.cloneElement(children, {
          isDropTarget: isOver && canDrop,
          canDrop,
          isDragging
        })}
        {isOver && canDrop && <DropOverlay />}
      </div>
    )
  }

  // Render folder tree recursively
  const renderFolderTree = (folder, index, depth = 0) => {
    const color = '#33becc' // Use consistent light blue/cyan color for all folders in custom view
    const isSelected = selectedNode?.id === folder.id
    const isExpanded = currentExpandedFolders.has(folder.id)
    const hasChildren =
      (folder.children && folder.children.length > 0) ||
      (folder.files && folder.files.length > 0)

    // Hide folders with zero matching results when search has returned results
    if (isSearching && searchResults && searchResults.length > 0) {
      const matching = getMatchingCountForFolder(folder)
      if (matching === 0) return null // 0 = confirmed no matches; -1 = unknown, keep visible
    }

    return (
      <TreeFolder key={folder.id} depth={depth}>
        <DroppableFolder folder={folder}>
          <FolderHeader
            isSelected={isSelected}
            folderColor={color}
            depth={depth}
            onClick={() => {
              onNodeSelect(folder)
            }}
            onContextMenu={
              canEdit ? e => handleContextMenu(e, folder) : undefined
            }
          >
            <ExpandIcon
              onClick={e => {
                e.stopPropagation()
                if (hasChildren) {
                  toggleFolderExpanded(folder.id)
                }
              }}
            >
              {hasChildren &&
                (isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />)}
            </ExpandIcon>
            <FolderIconStyled color={color}>
              {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
            </FolderIconStyled>

            <FolderNameWithCount>
              <span title={folder.name}>{folder.name}</span>
              {/* Show loaded indicator - only check enrichedFolders (custom view specific) */}
              {enrichedFolders?.has?.(folder.id) && (
                <Tooltip title='Files loaded and ready' placement='top'>
                  <LoadedIndicator>
                    <CheckCircleIcon
                      style={{ fontSize: 12, color: '#4caf50' }}
                    />
                  </LoadedIndicator>
                </Tooltip>
              )}
              {loadingCustomFolders.has(folder.id) && (
                <Tooltip title='Loading files...' placement='top'>
                  <LoadingIndicator>
                    <CircularProgress size={10} style={{ color: '#33becc' }} />
                  </LoadingIndicator>
                </Tooltip>
              )}
              {canEdit && !isSharePointView && (
                <FolderActions>
                  <IconButton
                    size='small'
                    onClick={e => {
                      e.stopPropagation()
                      handleContextMenu(e, folder)
                    }}
                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                  >
                    <MoreVertIcon fontSize='small' />
                  </IconButton>
                </FolderActions>
              )}
              <CategoryCount folderColor={color}>
                {(() => {
                  if (!isSearching) return `(${folder.fileCount || 0})`
                  const matching = getMatchingCountForFolder(folder)
                  if (matching < 0) return `(${folder.fileCount || 0})` // unknown — show normal count
                  return `(${matching})`
                })()}
              </CategoryCount>
              {isOwner && folder.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(folder.id) && (
                <FolderPermissionsIndicator
                  bp={bp}
                  userCount={folderUserCounts?.folders?.[folder.id] || 0}
                  folderId={folder.id}
                  folderType="custom"
                  customView={selectedView}
                  isOwner={isOwner}
                  onPermissionsUpdated={fetchFolderUserCounts}
                />
              )}
            </FolderNameWithCount>
          </FolderHeader>
        </DroppableFolder>
        {isExpanded && (
          <SubFoldersContainer>
            {/* Render child folders */}
            {hasChildren &&
              folder.children.map((child, childIndex) =>
                renderFolderTree(child, index, depth + 1)
              )}
            {/* Render files in this folder */}
            {folder.files &&
              folder.files.length > 0 &&
              folder.files.map((file, fileIndex) => {
                // Handle case where file might not be fully loaded yet
                if (!file || !file.id) return null
                // Skip files that haven't been enriched with full data yet
                if (!file.displayName && !file.display_name && !file.name)
                  return null

                return (
                  <FileItem
                    key={file.id}
                    depth={depth + 1}
                    onClick={() => onNodeSelect({ ...file, type: 'file' })}
                  >
                    <ExpandIcon>{/* Empty space for alignment */}</ExpandIcon>
                    <FileIconStyled>
                      <ArticleIcon />
                    </FileIconStyled>
                    <Tooltip title={file.displayName || file.display_name || file.name} placement='top'>
                      <FileName>
                        {file.displayName || file.display_name || file.name}
                        {isSharePointView && (
                          <Tooltip
                            title='Linked from SharePoint'
                            placement='right'
                          >
                            <LinkIcon
                              sx={{
                                ml: 0.5,
                                fontSize: '0.9em',
                                color: 'primary.main',
                                verticalAlign: 'middle'
                              }}
                            />
                          </Tooltip>
                        )}
                      </FileName>
                    </Tooltip>
                  </FileItem>
                )
              })}
            {/* Show loading indicator if files are still loading */}
            {filesLoading && isExpanded && (
              <FileItem
                depth={depth + 1}
                style={{ opacity: 0.6, cursor: 'default' }}
              >
                <ExpandIcon>{/* Empty space for alignment */}</ExpandIcon>
                <CircularProgress size={12} sx={{ mr: 0.5 }} />
                <FileName style={{ fontSize: '10px', fontStyle: 'italic' }}>
                  Loading files...
                </FileName>
              </FileItem>
            )}
          </SubFoldersContainer>
        )}
      </TreeFolder>
    )
  }

  if (initialLoading || loading) {
    return (
      <Container>
        <TreeContainer>
          <ViewSelector>
            <Skeleton variant='rectangular' width='100%' height={36} sx={{ borderRadius: 1 }} />
          </ViewSelector>
          <div style={{ padding: '8px 0' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px' }}>
                <Skeleton variant='circular' width={20} height={20} />
                <Skeleton variant='text' width={`${65 - i * 10}%`} height={20} />
              </div>
            ))}
          </div>
        </TreeContainer>

        {/* View Selector Dropdown Menu */}
        {customViews && !isMobile && (
          <Menu
            anchorEl={viewMenuAnchor}
            open={Boolean(viewMenuAnchor)}
            onClose={() => setViewMenuAnchor(null)}
            disablePortal={false}
            sx={{ zIndex: 20002 }}
            PaperProps={{
              sx: {
                minWidth: 200,
                mt: 0.5,
                zIndex: 20003,
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  py: 1
                }
              }
            }}
          >
            {customViews && customViews.length > 0 ? (
              customViews.map(view => (
                <MenuItem
                  key={view.id}
                  onClick={() => {
                    if (onViewSelect) onViewSelect(view)
                    setViewMenuAnchor(null)
                  }}
                  selected={selectedView?.id === view.id}
                >
                  {view.name}
                  {selectedView?.id === view.id && (
                    <CheckIcon sx={{ ml: 'auto', fontSize: 16 }} />
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>No views available</em>
              </MenuItem>
            )}
            <Divider />
            {[
              onViewCreate && (
                <MenuItem
                  key='create'
                  onClick={() => {
                    onViewCreate()
                    setViewMenuAnchor(null)
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} fontSize='small' />
                  Create New View
                </MenuItem>
              ),
              onSharePointIntegrate && isOwner && (
                <MenuItem
                  key='sharepoint'
                  onClick={() => {
                    onSharePointIntegrate()
                    setViewMenuAnchor(null)
                  }}
                >
                  <ShareIcon sx={{ mr: 1 }} fontSize='small' />
                  Connect SharePoint
                </MenuItem>
              ),
              onViewEdit && selectedView && (
                <MenuItem
                  key='edit'
                  onClick={() => {
                    onViewEdit(selectedView)
                    setViewMenuAnchor(null)
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize='small' />
                  Edit Current View
                </MenuItem>
              ),
              onViewDelete && selectedView && (
                <MenuItem
                  key='delete'
                  onClick={() => {
                    onViewDelete(selectedView?.id)
                    setViewMenuAnchor(null)
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize='small' />
                  Delete Current View
                </MenuItem>
              )
            ].filter(Boolean)}
          </Menu>
        )}
      </Container>
    )
  }

  // Always render the full interface - never show empty state

  return (
    <Container>
      <TreeContainer>
        {/* View Selector Dropdown Menu */}
        {customViews && (
          <Menu
            anchorEl={viewMenuAnchor}
            open={Boolean(viewMenuAnchor)}
            onClose={() => setViewMenuAnchor(null)}
            disablePortal={false}
            sx={{ zIndex: 20002 }}
            PaperProps={{
              sx: {
                minWidth: 200,
                mt: 0.5,
                zIndex: 20003,
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  py: 1
                }
              }
            }}
          >
            {customViews && customViews.length > 0 ? (
              customViews.map(view => (
                <MenuItem
                  key={view.id}
                  onClick={() => {
                    if (onViewSelect) onViewSelect(view)
                    setViewMenuAnchor(null)
                  }}
                  selected={selectedView?.id === view.id}
                >
                  {view.name}
                  {selectedView?.id === view.id && (
                    <CheckIcon sx={{ ml: 'auto', fontSize: 16 }} />
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>No views available</em>
              </MenuItem>
            )}
            <Divider />
            {[
              onViewCreate && (
                <MenuItem
                  key='create'
                  onClick={() => {
                    onViewCreate()
                    setViewMenuAnchor(null)
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} fontSize='small' />
                  Create New View
                </MenuItem>
              ),
              onSharePointIntegrate && isOwner && (
                <MenuItem
                  key='sharepoint'
                  data-tour-id='files.connect-sharepoint'
                  onClick={() => {
                    onSharePointIntegrate()
                    setViewMenuAnchor(null)
                  }}
                >
                  <ShareIcon sx={{ mr: 1 }} fontSize='small' />
                  Connect SharePoint
                </MenuItem>
              ),
              onViewEdit && selectedView && (
                <MenuItem
                  key='edit'
                  onClick={() => {
                    onViewEdit(selectedView)
                    setViewMenuAnchor(null)
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize='small' />
                  Edit Current View
                </MenuItem>
              ),
              onViewDelete && selectedView && (
                <MenuItem
                  key='delete'
                  onClick={() => {
                    onViewDelete(selectedView?.id)
                    setViewMenuAnchor(null)
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize='small' />
                  Delete Current View
                </MenuItem>
              )
            ].filter(Boolean)}
          </Menu>
        )}

        {customViews && (
          <ViewSelector>
            <ViewSelectorDropdown>
              <ViewSelectorLabel>View:</ViewSelectorLabel>
              <ViewDropdownButton onClick={openViewSelector}>
                <span>
                  {selectedView?.name ||
                    (customViews.length > 0 ? 'Select View' : 'No Views')}
                </span>
                <ExpandMoreIcon />
              </ViewDropdownButton>
            </ViewSelectorDropdown>
            {canEdit && (
              <Tooltip title='Manage views'>
                <IconButton
                  size='small'
                  data-tour-id='files.views-menu'
                  onClick={openViewSelector}
                  sx={{
                    color: theme => theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: 'primary.main'
                    }
                  }}
                >
                  <MoreVertIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </ViewSelector>
        )}

        {!initialLoading && customViews && customViews.length === 0 && (
          <EmptyViewsPanel>
            <EmptyViewsTitle>No Custom view created yet</EmptyViewsTitle>
            <EmptyViewsText>
              Create your first custom view to start organising files into folders.
            </EmptyViewsText>
            {canEdit ? (
              <EmptyViewsButton onClick={onViewCreate}>
                <AddIcon fontSize='small' />
                Create new view
              </EmptyViewsButton>
            ) : (
              <EmptyViewsHint>
                Ask an owner to create a custom view.
              </EmptyViewsHint>
            )}
          </EmptyViewsPanel>
        )}

        {isMobile && typeof document !== 'undefined' &&
          ReactDOM.createPortal(
            <>
              <MobileViewSheetOverlay $isOpen={viewSheetOpen} onClick={closeViewSelector} />
              <MobileViewSheet $isOpen={viewSheetOpen}>
                <MobileViewSheetHeader>
                  <MobileViewSheetTitle>Select view</MobileViewSheetTitle>
                  <MobileHeaderActions>
                    {onViewCreate && (
                      <MobileHeaderActionButton onClick={() => { onViewCreate(); setViewSheetOpen(false) }}>
                        <AddIcon />
                      </MobileHeaderActionButton>
                    )}
                    {onSharePointIntegrate && isOwner && (
                      <MobileHeaderActionButton onClick={() => { onSharePointIntegrate(); setViewSheetOpen(false) }} data-tour-id='files.connect-sharepoint'>
                        <ShareIcon />
                      </MobileHeaderActionButton>
                    )}
                    {onViewEdit && selectedView && (
                      <MobileHeaderActionButton onClick={() => { onViewEdit(selectedView); setViewSheetOpen(false) }}>
                        <EditIcon />
                      </MobileHeaderActionButton>
                    )}
                    {onViewDelete && selectedView && (
                      <MobileHeaderActionButton $danger onClick={() => { onViewDelete(selectedView?.id); setViewSheetOpen(false) }}>
                        <DeleteIcon />
                      </MobileHeaderActionButton>
                    )}
                    <MobileViewSheetClose onClick={closeViewSelector}>
                      <CloseIcon />
                    </MobileViewSheetClose>
                  </MobileHeaderActions>
                </MobileViewSheetHeader>
                <MobileViewSheetContent>
                  <MobileViewList>
                    {customViews && customViews.length > 0 ? (
                      customViews.map(view => (
                        <MobileViewItem
                          key={view.id}
                          $active={selectedView?.id === view.id}
                          onClick={() => {
                            if (onViewSelect) onViewSelect(view)
                            setViewSheetOpen(false)
                          }}
                        >
                          <span>{view.name}</span>
                          {selectedView?.id === view.id && <CheckIcon />}
                        </MobileViewItem>
                      ))
                    ) : (
                      <MobileViewEmpty>No views available</MobileViewEmpty>
                    )}
                  </MobileViewList>

                  {/* Actions moved to header on mobile */}
                </MobileViewSheetContent>
              </MobileViewSheet>
            </>,
            document.body
          )}
        <TreeHeader>
          <HeaderText>Folders</HeaderText>
          {canEdit && !isReadOnly && !isSharePointView && customViews && customViews.length > 0 && (
            <Tooltip title="Add parent folder" placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => onFolderCreate(null)}
                sx={{ color: 'primary.main' }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </TreeHeader>

        {/* SharePoint Sync Status */}
        {isSharePointView && (
          <SharePointSyncSection>
            {/* Compact Header with Status and Actions */}
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              sx={{ pl: 0.25, pr: 0.5, py: 0.75, backgroundColor: 'grey.50', borderRadius: 1 }}
            >
              <Box display='flex' alignItems='center' gap={0.75} sx={{ minWidth: 0 }}>
                <SharePointIcon size={18} />
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  SharePoint
                </Typography>
                <Chip
                  size='small'
                  icon={getSyncStatusIcon()}
                  label={(syncStatus.status || 'Unknown').toUpperCase()}
                  color={getSyncStatusColor()}
                  variant='filled'
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    flexShrink: 0,
                    '& .MuiChip-icon': {
                      fontSize: '0.75rem',
                      marginLeft: '2px',
                      marginRight: '-4px'
                    },
                    '& .MuiChip-label': {
                      px: 0.6
                    }
                  }}
                />
              </Box>
              <Box display='flex' alignItems='center' gap={0.25} sx={{ flexShrink: 0 }}>
                <SharePointViewInfo
                  view={selectedView}
                  bpId={bp?.id}
                  syncStatus={syncStatus}
                  onChangeFolder={isOwner ? () => setChangeFolderOpen(true) : undefined}
                />
                {isOwner && (
                  <SharePointChangeFolderModal
                    open={changeFolderOpen}
                    onClose={() => setChangeFolderOpen(false)}
                    customView={selectedView}
                    buildingPassportId={bp?.id}
                    onChanged={() => {
                      setSyncStatus({ status: 'pending', message: 'Re-syncing from the new folder' })
                      setPollingSyncStatus(true)
                      if (onCustomViewRefresh) onCustomViewRefresh()
                      if (onRefresh) onRefresh()
                    }}
                  />
                )}
                <Tooltip title='Refresh view' placement='top'>
                  <IconButton
                    size='small'
                    aria-label='Refresh view'
                    onClick={() => { if (onRefresh) onRefresh() }}
                    disabled={loading}
                    sx={{ p: 0.5, color: '#4d4d56', '&:hover': { backgroundColor: 'action.hover', color: '#12132e' } }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Sync with SharePoint' placement='top'>
                  <IconButton
                    size='small'
                    aria-label='Sync with SharePoint'
                    onClick={handleManualSync}
                    disabled={loading || syncStatus.status === 'running'}
                    sx={{ p: 0.5, color: '#4d4d56', '&:hover': { backgroundColor: 'action.hover', color: '#12132e' } }}
                  >
                    <SyncIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {isOwner && (
                  <Tooltip title='Access control' placement='top'>
                    <IconButton
                      size='small'
                      aria-label='Access control'
                      onClick={() => { if (onSharePointAccessEdit) onSharePointAccessEdit(selectedView) }}
                      disabled={loading}
                      sx={{ p: 0.5, color: '#4d4d56', '&:hover': { backgroundColor: 'action.hover', color: '#12132e' } }}
                    >
                      <SecurityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Last sync info - very subtle */}
            <Typography
              variant='caption'
              color='textSecondary'
              sx={{
                display: 'block',
                fontSize: '0.65rem',
                mt: 0.5,
                ml: 0.5,
                opacity: 0.7
              }}
            >
              Last sync: {formatLastSync()}
            </Typography>

            {/* Sync Progress - detailed */}
            {(syncStatus.status === 'pending' ||
              syncStatus.status === 'running') && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress sx={{ height: 3, mb: 1 }} />
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: '0.75rem',
                    display: 'block',
                    fontWeight: 'bold'
                  }}
                >
                  Syncing with SharePoint...
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                >
                  Retrieving files from SharePoint. This may take several minutes.
                </Typography>
                <Alert
                  severity='warning'
                  sx={{ mt: 1, py: 0.5, fontSize: '0.7rem' }}
                >
                  <Typography variant='caption' sx={{ fontSize: '0.7rem' }}>
                    <strong>Important:</strong> File processing happens in the
                    background and may take additional time to complete. The
                    page will refresh automatically when the sync is done.
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Error Display - detailed */}
            {syncStatus.status === 'error' && (
              <Alert severity='error' sx={{ mt: 1, py: 0.5 }}>
                <Typography
                  variant='body2'
                  sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                >
                  Sync Failed
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                >
                  {syncStatus.message ||
                    'Unable to sync with SharePoint. Please try again.'}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                >
                  If this problem persists, please contact support.
                </Typography>
              </Alert>
            )}

            {/* Rate Limited Display - detailed */}
            {syncStatus.status === 'rate_limited' && (
              <Alert severity='warning' sx={{ mt: 1, py: 0.5 }}>
                <Typography
                  variant='body2'
                  sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                >
                  Sync Limit Reached
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                >
                  {syncStatus.message ||
                    'Daily sync limit reached. You can perform up to 3 manual syncs per day per SharePoint view.'}
                </Typography>
                {syncStatus.remaining_syncs !== undefined && (
                  <Typography
                    variant='caption'
                    sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                  >
                    Remaining syncs today: {syncStatus.remaining_syncs}
                  </Typography>
                )}
                {syncStatus.next_reset && (
                  <Typography
                    variant='caption'
                    sx={{
                      fontSize: '0.7rem',
                      display: 'block',
                      mt: 0.5,
                      fontStyle: 'italic'
                    }}
                  >
                    Resets: {syncStatus.next_reset}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Success message with detailed file/folder count */}
            {(syncStatus.status === 'success' ||
              syncStatus.status === 'completed' ||
              syncStatus.status === 'partial_success') && (
              <Alert
                severity='success'
                sx={{ mt: 1, py: 0.5 }}
              >
                <Typography
                  variant='body2'
                  sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                >
                  SharePoint Sync Completed
                </Typography>
                {syncStatus.syncResults && (
                  <Typography
                    variant='caption'
                    sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                  >
                    {syncStatus.syncResults.newFolders?.length > 0 && (
                      <>
                        • Created {syncStatus.syncResults.newFolders.length} new
                        folder(s)
                        <br />
                      </>
                    )}
                    {syncStatus.syncResults.total > 0 && (
                      <>
                        • Found {syncStatus.syncResults.total} file(s) in
                        SharePoint
                        <br />
                      </>
                    )}
                    {syncStatus.syncResults.newFiles > 0 && (
                      <>
                        • Added {syncStatus.syncResults.newFiles} new file(s)
                        <br />
                      </>
                    )}
                    {syncStatus.syncResults.updatedFiles > 0 && (
                      <>
                        • Updated {syncStatus.syncResults.updatedFiles} existing
                        file(s)
                        <br />
                      </>
                    )}
                    {syncStatus.syncResults.deletedFiles > 0 && (
                      <>
                        • Removed {syncStatus.syncResults.deletedFiles} file(s)
                        <br />
                      </>
                    )}
                    {syncStatus.syncResults.success > 0 &&
                      !syncStatus.syncResults.newFiles &&
                      !syncStatus.syncResults.updatedFiles && (
                        <>
                          • Successfully uploaded{' '}
                          {syncStatus.syncResults.success} file(s)
                          <br />
                        </>
                      )}
                    {syncStatus.syncResults.failed > 0 && (
                      <>
                        • Some files could not be synced{' '}
                        <Link
                          component='button'
                          variant='caption'
                          onClick={() => setFailedFilesExpanded(!failedFilesExpanded)}
                          sx={{
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            verticalAlign: 'baseline'
                          }}
                        >
                          {failedFilesExpanded ? 'Hide details' : 'View details'}
                        </Link>
                        <Collapse in={failedFilesExpanded}>
                          <Box sx={{ pl: 2, mt: 0.5 }}>
                            {syncStatus.syncResults.failures?.map((failure, index) => (
                              <Typography
                                key={index}
                                variant='caption'
                                sx={{
                                  fontSize: '0.65rem',
                                  color: 'text.secondary',
                                  display: 'block'
                                }}
                              >
                                - {failure.file || failure.name || `File ${index + 1}`}
                              </Typography>
                            ))}
                          </Box>
                        </Collapse>
                        <br />
                      </>
                    )}
                    {syncStatus.syncResults.unsupported > 0 && (
                      <>
                        • Skipped {syncStatus.syncResults.unsupported} file(s)
                        (unsupported format)
                        <IconButton
                          size='small'
                          onClick={() => setUnsupportedFilesModalOpen(true)}
                          sx={{
                            ml: 0.5,
                            p: 0.5,
                            verticalAlign: 'middle'
                          }}
                        >
                          <ErrorIcon
                            sx={{
                              fontSize: '16px',
                              color: 'warning.main'
                            }}
                          />
                        </IconButton>
                        <br />
                      </>
                    )}
                  </Typography>
                )}

                {/* Dynamic status message based on actual file count */}
                {syncStatus.syncResults ? (
                  (() => {
                    // For incremental sync, only newFiles increase the count (updates don't change count, deletes decrease it)
                    // For full sync, use success count
                    const expectedNewFiles =
                      syncStatus.syncResults.newFiles || 0
                    const expectedCount =
                      expectedNewFiles > 0
                        ? expectedNewFiles
                        : syncStatus.syncResults.success || 0
                    const currentCount = actualFileCount

                    // For incremental sync with only updates/deletes, don't show processing message
                    const hasNewFiles =
                      expectedNewFiles > 0 ||
                      (syncStatus.syncResults.success > 0 &&
                        !syncStatus.syncResults.hasOwnProperty('newFiles'))
                    const allProcessed =
                      !hasNewFiles || currentCount >= expectedCount

                    if (allProcessed && currentCount > 0) {
                      return (
                        <Typography
                          variant='caption'
                          sx={{
                            fontSize: '0.7rem',
                            display: 'block',
                            mt: 1,
                            fontWeight: 'bold',
                            color: 'success.dark'
                          }}
                        >
                          ✓ {currentCount} file(s) are now available in the
                          platform.
                        </Typography>
                      )
                    } else if (currentCount < expectedCount) {
                      return (
                        <Typography
                          variant='caption'
                          sx={{
                            fontSize: '0.7rem',
                            display: 'block',
                            mt: 1,
                            fontWeight: 'bold',
                            color: 'warning.dark'
                          }}
                        >
                          Processing: {currentCount} of {expectedCount} file(s)
                          are currently visible. The remaining files are being
                          processed in the background.
                        </Typography>
                      )
                    } else {
                      return (
                        <Typography
                          variant='caption'
                          sx={{
                            fontSize: '0.7rem',
                            display: 'block',
                            mt: 1,
                            fontWeight: 'bold',
                            color: 'warning.dark'
                          }}
                        >
                          Files are being processed in the background. Please
                          refresh in a few moments.
                        </Typography>
                      )
                    }
                  })()
                ) : (
                  <Typography
                    variant='caption'
                    sx={{
                      fontSize: '0.7rem',
                      display: 'block',
                      mt: 0.5,
                      fontWeight: 'bold',
                      color: 'success.dark'
                    }}
                  >
                    {syncStatus.message || 'Sync completed successfully'}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Empty folders note - subtle */}
            {(syncStatus.status === 'success' ||
              syncStatus.status === 'completed' ||
              syncStatus.status === 'partial_success') && (
              <Typography
                variant='caption'
                color='textSecondary'
                sx={{
                  fontSize: '0.65rem',
                  display: 'block',
                  mt: 1,
                  fontStyle: 'italic'
                }}
              >
                Note: Empty SharePoint folders are not displayed. Only folders
                containing files will appear.
              </Typography>
            )}
          </SharePointSyncSection>
        )}

        <TreeContent>
          <FolderTree>
            {/* SharePoint empty state when sync is pending */}
            {isSharePointView &&
              (!treeData?.folders || treeData.folders.length === 0) &&
              syncStatus.status === 'pending' && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CloudSyncIcon
                    sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                  />
                  <Typography variant='h6' color='textSecondary' gutterBottom>
                    SharePoint Sync Pending
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    Waiting for initial sync to begin. This may take a few
                    moments.
                  </Typography>
                </Box>
              )}

            {/* SharePoint empty state when syncing */}
            {isSharePointView &&
              (!treeData?.folders || treeData.folders.length === 0) &&
              syncStatus.status === 'running' && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant='h6' color='textSecondary' gutterBottom>
                    Syncing SharePoint Files
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {syncStatus.message ||
                      'Retrieving files and folders from SharePoint...'}
                  </Typography>
                </Box>
              )}

            {/* SharePoint empty state when sync completed but no folders */}
            {isSharePointView &&
              (!treeData?.folders || treeData.folders.length === 0) &&
              (syncStatus.status === 'success' ||
                syncStatus.status === 'completed') &&
              !loading && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <FolderOpenIcon
                    sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                  />
                  <Typography variant='h6' color='textSecondary' gutterBottom>
                    No Folders Found
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    The SharePoint location appears to be empty or contains only
                    files in the root directory.
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ mt: 1, fontSize: '0.65rem', opacity: 0.7 }}
                  >
                    (Empty folders are not shown)
                  </Typography>
                </Box>
              )}

            {/* Loading state when refreshing */}
            {loading && isSharePointView && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={30} sx={{ mb: 2 }} />
                <Typography variant='body2' color='textSecondary'>
                  Refreshing view...
                </Typography>
              </Box>
            )}

            {/* Regular folders with tree structure */}
            {!loading &&
              treeData?.folders?.map((folder, index) =>
                renderFolderTree(folder, index)
              )}

            {/* Unassigned folder - show for regular custom views only, not SharePoint */}
            {!loading && treeData?.unorganized && !isSharePointView && !(isSearching && searchResults && searchResults.length > 0 && getMatchingCountForFolder(treeData.unorganized) === 0) && (
            /* Note: getMatchingCountForFolder returns -1 when fileIds unavailable (cache), so folder stays visible */
              <TreeFolder key='unorganized'>
                <DroppableFolder folder={{ id: null, name: 'Unassigned' }}>
                  <FolderHeader
                    isSelected={selectedNode?.id === 'unorganized'}
                    folderColor='#FF9800'
                    onClick={() => onNodeSelect(treeData?.unorganized)}
                  >
                    <ExpandIcon>
                      {/* Empty space to align with folders that have expand icons */}
                    </ExpandIcon>
                    <FolderIconStyled color='#FF9800'>
                      <UnarchiveIcon />
                    </FolderIconStyled>
                    <FolderNameWithCount>
                      <Tooltip title='Unassigned' placement='top'>
                        <span>Unassigned</span>
                      </Tooltip>
                      {/* Show loaded indicator for Unassigned folder - only check enrichedFolders */}
                      {enrichedFolders?.has?.('unorganized') && (
                        <Tooltip title='Files loaded and ready' placement='top'>
                          <LoadedIndicator>
                            <CheckCircleIcon
                              style={{ fontSize: 12, color: '#4caf50' }}
                            />
                          </LoadedIndicator>
                        </Tooltip>
                      )}
                      {loadingCustomFolders.has('unorganized') && (
                        <Tooltip title='Loading files...' placement='top'>
                          <LoadingIndicator>
                            <CircularProgress
                              size={10}
                              style={{ color: '#33becc' }}
                            />
                          </LoadingIndicator>
                        </Tooltip>
                      )}
                      <CategoryCount folderColor='#FF9800'>
                        {(() => {
                          if (!isSearching) return `(${treeData?.unorganized?.fileCount || 0})`
                          const matching = getMatchingCountForFolder(treeData.unorganized)
                          if (matching < 0) return `(${treeData?.unorganized?.fileCount || 0})`
                          return `(${matching})`
                        })()}
                      </CategoryCount>
                    </FolderNameWithCount>
                  </FolderHeader>
                </DroppableFolder>
              </TreeFolder>
            )}
          </FolderTree>
        </TreeContent>

        {/* Context Menu for Folders */}
        <Menu
          anchorReference='anchorPosition'
          anchorPosition={
            contextMenuAnchor && typeof contextMenuAnchor === 'object'
              ? {
                  top: contextMenuAnchor.mouseY,
                  left: contextMenuAnchor.mouseX
                }
              : undefined
          }
          open={Boolean(contextMenuAnchor)}
          onClose={() => setContextMenuAnchor(null)}
        >
          {selectedView?.integrationType !== 'sharepoint' && (
            <MenuItem
              key="add-subfolder"
              onClick={() => {
                onFolderCreate(contextMenuItem?.id)
                setContextMenuAnchor(null)
              }}
            >
              <AddIcon sx={{ mr: 1 }} fontSize='small' />
              Add Subfolder
            </MenuItem>
          )}

          {selectedView?.integrationType !== 'sharepoint' && [
            <MenuItem
              key="edit-folder"
              onClick={() => {
                onFolderEdit(contextMenuItem)
                setContextMenuAnchor(null)
              }}
            >
              <EditIcon sx={{ mr: 1 }} fontSize='small' />
              Edit Folder
            </MenuItem>,

            <MenuItem
              key="delete-folder"
              onClick={() => {
                onFolderDelete(contextMenuItem?.id)
                setContextMenuAnchor(null)
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1 }} fontSize='small' />
              Delete Folder
            </MenuItem>
          ]}
        </Menu>

        {/* View Selector Dropdown Menu */}
        {customViews && (
          <Menu
            anchorEl={viewMenuAnchor}
            open={Boolean(viewMenuAnchor)}
            onClose={() => setViewMenuAnchor(null)}
            PaperProps={{
              sx: {
                minWidth: 200,
                mt: 0.5,
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  py: 1
                }
              }
            }}
          >
            {customViews && customViews.length > 0 ? (
              customViews.map(view => (
                <MenuItem
                  key={view.id}
                  onClick={() => {
                    if (onViewSelect) onViewSelect(view)
                    setViewMenuAnchor(null)
                  }}
                  selected={selectedView?.id === view.id}
                >
                  {view.name}
                  {selectedView?.id === view.id && (
                    <CheckIcon sx={{ ml: 'auto', fontSize: 16 }} />
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>No views available</em>
              </MenuItem>
            )}
            <Divider />
            {[
              onViewCreate && (
                <MenuItem
                  key='create'
                  onClick={() => {
                    onViewCreate()
                    setViewMenuAnchor(null)
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} fontSize='small' />
                  Create New View
                </MenuItem>
              ),
              onSharePointIntegrate && isOwner && (
                <MenuItem
                  key='sharepoint'
                  onClick={() => {
                    onSharePointIntegrate()
                    setViewMenuAnchor(null)
                  }}
                >
                  <ShareIcon sx={{ mr: 1 }} fontSize='small' />
                  Connect SharePoint
                </MenuItem>
              ),
              onViewEdit && selectedView && (
                <MenuItem
                  key='edit'
                  onClick={() => {
                    onViewEdit(selectedView)
                    setViewMenuAnchor(null)
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize='small' />
                  Edit Current View
                </MenuItem>
              ),
              onViewDelete && selectedView && (
                <MenuItem
                  key='delete'
                  onClick={() => {
                    onViewDelete(selectedView?.id)
                    setViewMenuAnchor(null)
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize='small' />
                  Delete Current View
                </MenuItem>
              )
            ].filter(Boolean)}
          </Menu>
        )}
      </TreeContainer>

      {/* Unsupported Files Modal */}
      <Dialog
        open={unsupportedFilesModalOpen}
        onClose={() => setUnsupportedFilesModalOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          Unsupported Files
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            The following files were skipped because their formats are not
            supported
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {syncStatus?.syncResults?.unsupportedFiles &&
          syncStatus.syncResults.unsupportedFiles.length > 0 ? (
            <>
              <List dense>
                {syncStatus.syncResults.unsupportedFiles.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={file.name || 'Unknown file'}
                      secondary={`Format: ${file.extension?.toUpperCase() || 'Unknown'} • ${file.reason || 'Unsupported format'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                >
                  Supported formats:
                </Typography>
                <Typography variant='caption'>
                  PDF, DWG, DOC, DOCX, XLS, XLSX, XLSM, JPG, JPEG, TXT, PNG,
                  GIF, SVG, MSG
                </Typography>
              </Box>
            </>
          ) : (
            <Typography>No unsupported files information available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnsupportedFilesModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resyncConfirmOpen}
        onClose={() => setResyncConfirmOpen(false)}
      >
        <DialogTitle>Clear and re-sync SharePoint</DialogTitle>
        <DialogContent>
          <Typography>
            This clears all files currently synced from SharePoint and runs a
            full re-sync from scratch. The files in SharePoint are not affected.
            Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResyncConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmClearAndResync}
            color='error'
            variant='contained'
          >
            Clear and re-sync
          </Button>
        </DialogActions>
      </Dialog>

      <SharePointReauthModal
        open={reauthModalOpen}
        onClose={() => setReauthModalOpen(false)}
        customViewId={selectedView?.id}
        buildingPassportId={bp?.id}
        bp={bp}
        onSuccess={() => {
          setReauthModalOpen(false)
          setSyncStatus({ status: 'pending', message: 'Re-authenticated. Syncing...' })
          handleManualSync()
        }}
      />
    </Container>
  )
}

export default CustomViewFolders

// Styled Components
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const TreeContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  border: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#e0e0e0'};
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme?.colors?.secondary || '#33becc'};
    border-radius: 8px 8px 0 0;
  }
`

const TreeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid
    ${({ theme }) => theme?.colors?.lightGrey || '#e0e0e0'};
  padding: 2px 4px;
  flex-shrink: 0;
`

const HeaderText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  text-transform: uppercase;
  letter-spacing: 1px;
`

const TreeContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px;
  min-height: 0;
`

const ViewSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid
    ${({ theme }) => theme?.colors?.lightGrey || '#e0e0e0'};
  margin-bottom: 0;
  flex-shrink: 0;
`

const ViewSelectorDropdown = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`

const ViewSelectorLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.grey?.[200] || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ViewDropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#e0e0e0'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  min-width: 150px;
  justify-content: space-between;

  &:hover {
    background: ${({ theme }) => theme?.colors?.backgroundColor || '#f8f9fa'};
    border-color: ${({ theme }) => theme?.colors?.secondary || '#33becc'};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme?.colors?.grey?.[100] || '#999'};
    transition: transform 0.2s ease;
  }

  &:hover svg {
    color: ${({ theme }) => theme?.colors?.secondary || '#33becc'};
  }
`

const FolderTree = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  min-height: min-content;
`

const TreeFolder = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  margin-left: ${({ depth }) => (depth ? `${depth * 10}px` : '0')};
`

const SubFoldersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const FolderIconStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;

  svg {
    color: ${({ color }) => color};
    font-size: 14px;
    transition: all 0.2s ease;
  }
`

const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2px;
  height: 22px;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({
    isSelected,
    folderColor,
    isDropTarget,
    isDragging
  }) =>
    isDropTarget
      ? `${folderColor}30`
      : isDragging
        ? `${folderColor}10`
        : isSelected
          ? `${folderColor}15`
          : 'transparent'};
  border: ${({ isSelected, folderColor, isDropTarget, isDragging }) =>
    isDropTarget
      ? `2px solid ${folderColor}`
      : isDragging
        ? `1px dashed ${folderColor}80`
        : isSelected
          ? `1px solid ${folderColor}`
          : '1px solid transparent'};
  margin: 0;
  position: relative;

  &:hover {
    background-color: ${({ isSelected, folderColor, theme, isDropTarget }) =>
      isDropTarget
        ? `${folderColor}30`
        : isSelected
          ? `${folderColor}20`
          : `${theme?.colors?.lightGrey || '#f0f0f0'}`};
    transform: ${({ isDropTarget }) =>
      isDropTarget ? 'translateX(2px)' : 'translateX(1px)'};
    box-shadow: ${({ isDropTarget }) =>
      isDropTarget
        ? '0 2px 8px rgba(51, 190, 204, 0.3)'
        : '0 1px 4px rgba(0, 0, 0, 0.1)'};
  }

  svg {
    color: ${({ folderColor }) => folderColor};
    font-size: 16px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.05);
  }
`

const ExpandIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 2px;
  flex-shrink: 0;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  border-radius: 2px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ onClick }) =>
      onClick ? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
  }

  svg {
    font-size: 14px;
    color: #666;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: ${({ onClick }) => (onClick ? 'scale(1.2)' : 'none')};
  }
`

const FolderNameWithCount = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  > span:first-child {
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }
`

const CategoryCount = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.secondary || '#33becc'};
  background: ${({ theme }) => theme?.colors?.secondary || '#33becc'}10;
  padding: 2px 4px;
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
`

const FolderActions = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 6px;

  ${FolderHeader}:hover & {
    opacity: 1;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`

const EmptyState = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: #666;
  padding: 60px 40px;
  width: 80%;
  max-width: 400px;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border: 2px dashed ${({ theme }) => theme?.colors?.lightGrey || '#e0e0e0'};
    border-radius: 12px;
    z-index: -1;
  }
`

const DropOverlay = styled.div`
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  background-color: rgba(51, 190, 204, 0.08);
  border: 2px solid ${({ theme }) => theme?.colors?.secondary || '#33becc'};
  border-radius: 3px;
  pointer-events: none;
  z-index: 1;
`

const DropIcon = styled.div`
  font-size: 16px;
  margin-bottom: 4px;
`

const DropText = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SharePointSyncSection = styled.div`
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 4px;
  margin-bottom: 12px;
`

const DropHint = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(51, 190, 204, 0.05);
  border: 1px dashed rgba(51, 190, 204, 0.4);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 5;
`

const DropHintText = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: rgba(51, 190, 204, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FileItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2px;
  height: 20px;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  margin-left: ${({ depth }) => (depth ? `${depth * 10}px` : '0')};

  &:hover {
    background-color: ${({ theme }) => theme?.colors?.lightGrey || '#f0f0f0'};
    transform: translateX(1px);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    color: #666;
    font-size: 14px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.05);
  }
`

const FileIconStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;

  svg {
    color: #666;
    font-size: 14px;
    transition: all 0.2s ease;
  }
`

const FileName = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`

const LoadedIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-left: 4px;
  margin-right: 2px;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-left: 4px;
  margin-right: 2px;
`

const MobileViewSheetOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 20010;
  }
`

const MobileViewSheet = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
    z-index: 20011;
    transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
    transition: transform 0.25s ease;
    max-height: 70vh;
    flex-direction: column;
    overflow: hidden;
  }
`

const MobileViewSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
`

const MobileViewSheetTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0c1229;
`

const MobileViewSheetClose = styled.button`
  border: none;
  background: #f3f4f6;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
`

const MobileHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MobileHeaderActionButton = styled.button`
  border: 1px solid ${({ $danger }) => ($danger ? '#fecaca' : '#e5e7eb')};
  background: ${({ $danger }) => ($danger ? '#fef2f2' : '#fff')};
  color: ${({ $danger }) => ($danger ? '#dc2626' : '#111827')};
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 18px;
  }
`

const MobileViewSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const MobileViewList = styled.div`
  overflow-y: auto;
  padding: 8px 12px;
  max-height: 40vh;
`

const MobileViewItem = styled.button`
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? '#c24a2e' : '#e5e7eb')};
  background: ${({ $active }) => ($active ? '#fff5f2' : '#fff')};
  color: #111827;
  padding: 10px 12px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  margin-bottom: 8px;
`

const MobileViewEmpty = styled.div`
  padding: 12px;
  color: #6b7280;
  font-size: 14px;
`

const EmptyViewsPanel = styled.div`
  margin: 12px 12px 16px;
  padding: 16px;
  border: 1px dashed #e5e7eb;
  background: #f9fafb;
  border-radius: 12px;
`

const EmptyViewsTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #111827;
  margin-bottom: 6px;
`

const EmptyViewsText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 12px;
`

const EmptyViewsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #c24a2e;
  color: #fff;
  border: none;
  font-weight: 600;
  cursor: pointer;
`

const EmptyViewsHint = styled.div`
  font-size: 12px;
  color: #6b7280;
`
